import { c as _c } from "react/compiler-runtime";
import { cn } from "cn";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type * as React from "react";
type TableSort = "asc" | "desc" | "none";
const ARIA_SORT = {
  asc: "ascending",
  desc: "descending",
  none: "none"
} as const;
const SORT_ICONS = {
  asc: ArrowUp,
  desc: ArrowDown,
  none: ArrowUpDown
} as const;
const SCROLL_SHADOW = cn("pointer-events-none absolute inset-y-0 w-8 opacity-0", "from-background to-transparent");

/**
 * A table that scrolls horizontally when wider than its container, with
 * each edge that has columns hidden past it fading into the background.
 */
function Table(t0) {
  const $ = _c(12);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full border-collapse", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="table-container" className="w-full overflow-x-auto [scroll-timeline:--scroll-x_inline]"><table data-slot="table" className={t1} {...props} /></div>;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div aria-hidden="true" className={cn(SCROLL_SHADOW, "left-0 bg-linear-to-r scroll-shadow-start")} />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div aria-hidden="true" className={cn(SCROLL_SHADOW, "right-0 bg-linear-to-l scroll-shadow-end")} />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2) {
    t5 = <div className="relative w-full [timeline-scope:--scroll-x]">{t2}{t3}{t4}</div>;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function TableHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("[&_tr]:hover:bg-transparent", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <thead data-slot="table-header" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function TableBody(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("[&_tr:last-child]:border-b-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <tbody data-slot="table-body" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function TableRow(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-b border-border transition-colors", "hover:bg-accent", "data-[state=selected]:bg-muted", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <tr data-slot="table-row" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type TableHeadProps = React.ComponentProps<"th"> & {
  sort?: TableSort;
  onSort?: React.MouseEventHandler<HTMLButtonElement>;
};

/**
 * A column header. With `sort` set, the content renders inside a button that
 * calls `onSort`, next to a direction icon, and the cell carries the matching
 * `aria-sort`.
 */
function TableHead(t0) {
  const $ = _c(20);
  let children;
  let className;
  let onSort;
  let props;
  let sort;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sort,
      onSort,
      scope: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onSort;
    $[4] = props;
    $[5] = sort;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    onSort = $[3];
    props = $[4];
    sort = $[5];
    t1 = $[6];
  }
  const scope = t1 === undefined ? "col" : t1;
  const SortIcon = sort ? SORT_ICONS[sort] : null;
  const t2 = sort ? ARIA_SORT[sort] : undefined;
  let t3;
  if ($[7] !== className) {
    t3 = cn("h-10 px-3 text-left align-middle whitespace-nowrap", "heading-xs text-muted-foreground", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== SortIcon || $[10] !== children || $[11] !== onSort || $[12] !== sort) {
    t4 = SortIcon ? <button type="button" onClick={onSort} className={cn("inline-flex cursor-pointer items-center gap-1 rounded-sm", "outline-none transition-colors hover:text-foreground", "focus-visible:ring-[3px] focus-visible:ring-ring/50", sort !== "none" && "text-foreground")}>{children}<SortIcon aria-hidden="true" className={cn("size-4", sort === "none" && "text-muted-foreground")} /></button> : children;
    $[9] = SortIcon;
    $[10] = children;
    $[11] = onSort;
    $[12] = sort;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== props || $[15] !== scope || $[16] !== t2 || $[17] !== t3 || $[18] !== t4) {
    t5 = <th data-slot="table-head" scope={scope} aria-sort={t2} className={t3} {...props}>{t4}</th>;
    $[14] = props;
    $[15] = scope;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function TableCell(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-3 py-2 align-middle", "body-md text-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <td data-slot="table-cell" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, type TableHeadProps, type TableSort };